import styled, { css, keyframes } from 'styled-components';

const fadeSlideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const motionSafe = (styles: ReturnType<typeof css>) => css`
    @media (prefers-reduced-motion: no-preference) {
        ${styles}
    }
`;

export const Wrapper = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    padding: 20px;
    min-height: 100vh;
    width: 100%;
    max-width: 440px;
    margin: 0 auto;
    box-sizing: border-box;
    overflow-x: hidden;
    background: #ffffff;

    @media screen and (max-width: 769px) {
        max-width: 700px;
        margin: 0;
    }

    @media screen and (min-width: 768px) {
        max-width: 1080px;
        padding: 32px 40px 48px;
        min-height: auto;
    }

    @media screen and (min-width: 1200px) {
        padding: 40px 56px 56px;
    }
`;

export const Content = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;

    ${motionSafe(css`
        animation: ${fadeSlideUp} 0.35s ease-out;
    `)}

    @media screen and (min-width: 768px) {
        display: flex;
        gap: 24px;
    }
`;

export const Section = styled.div<{
    $index?: number;
    $variant?: 'readonly' | 'editable' | 'tnc';
}>`
    width: 100%;

    ${({ $index }) =>
        typeof $index === 'number' &&
        motionSafe(css`
            animation: ${fadeSlideUp} 0.35s ease-out backwards;
            animation-delay: ${Math.min($index, 4) * 0.06}s;
        `)}

    @media screen and (min-width: 768px) {
        ${({ $variant }) =>
            $variant &&
            css`
                grid-column: auto;
            `}
    }
`;

export const HeroCard = styled.div`
    margin-top: 20px;
    padding: 24px 20px 22px;
    border-radius: 24px;
    background: radial-gradient(
            circle at top right,
            rgba(15, 164, 87, 0.12),
            transparent 34%
        ),
        linear-gradient(180deg, #ffffff 0%, #f7fbf8 100%);
    border: 1px solid #dcefe3;

    @media screen and (min-width: 768px) {
        margin-top: 12px;
        padding: 28px 28px 24px;
    }
`;

export const HeroEyebrow = styled.div`
    color: #0d8e4b;
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

export const Title = styled.div`
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: #040222;
    margin-top: 20px;

    @media screen and (min-width: 768px) {
        margin-top: 12px;
        font-size: 28px;
        line-height: 36px;
    }
`;

export const Subtitle = styled.p`
    margin: 10px 0 0;
    max-width: 760px;
    color: #5b5675;
    font-size: 14px;
    line-height: 22px;

    @media screen and (min-width: 768px) {
        font-size: 15px;
        line-height: 24px;
    }
`;

export const Card = styled.div<{ $variant?: 'readonly' | 'editable' | 'tnc' }>`
    margin-top: 20px;
    padding: 20px;
    background: #ffffff;
    border: 1px solid #e7e7f0;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(16, 18, 35, 0.04);

    ${({ $variant }) =>
        $variant === 'editable' &&
        css`
            border-color: #d7e8fb;
            background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
        `}

    ${({ $variant }) =>
        $variant === 'tnc' &&
        css`
            border-color: #dcefe3;
            background: linear-gradient(180deg, #ffffff 0%, #f8fcf9 100%);
        `}

    @media screen and (min-width: 768px) {
        padding: 24px;
        border-radius: 20px;
        min-height: 100%;
    }
`;

export const CardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
`;

export const SectionTitle = styled.h2`
    margin: 0;
    color: #040222;
    font-size: 16px;
    line-height: 22px;
    font-weight: 600;
`;

export const SectionBadge = styled.div<{
    $variant?: 'readonly' | 'editable' | 'tnc';
}>`
    flex-shrink: 0;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 11px;
    line-height: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #040222;
    background: #eef0f4;

    ${({ $variant }) =>
        $variant === 'editable' &&
        css`
            color: #0f5fb8;
            background: #e8f2ff;
        `}

    ${({ $variant }) =>
        $variant === 'tnc' &&
        css`
            color: #0d8e4b;
            background: #e8f7ee;
        `}
`;

export const FieldsGrid = styled.div<{
    $variant?: 'readonly' | 'editable' | 'tnc';
}>`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
`;

export const InputWrapper = styled.div<{ $fullWidth?: boolean }>`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }

    @media screen and (min-width: 768px) {
        margin-bottom: 0;

        ${({ $fullWidth }) =>
            $fullWidth &&
            css`
                grid-column: 1 / -1;
            `}
    }
`;

export const PreviewWrapper = styled.div<{ $fullWidth?: boolean }>`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }

    @media screen and (min-width: 768px) {
        margin-bottom: 0;

        ${({ $fullWidth }) =>
            $fullWidth &&
            css`
                grid-column: 1 / -1;
            `}
    }
`;

export const PreviewLabel = styled.div`
    color: #5b5675;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    margin-bottom: 8px;
`;

export const PreviewCard = styled.div`
    width: 100%;
    min-height: 180px;
    padding: 12px;
    border: 1px solid #e7e7f0;
    border-radius: 12px;
    background: #f7f8fa;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: auto;
        max-height: 240px;
        object-fit: contain;
        border-radius: 8px;
        display: block;
    }

    @media screen and (min-width: 768px) {
        min-height: 220px;
        padding: 16px;
    }
`;

export const TncControlRow = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 16px;
`;

export const CheckBoxFlex = styled.div`
    display: flex;
    min-height: 20px;
    padding-top: 2px;
`;

export const TncText = styled.div<{ $disabled?: boolean }>`
    color: ${({ $disabled }) => ($disabled ? '#9a97ad' : '#040222')};
    font-size: 14px;
    line-height: 20px;
`;

export const TncPreviewSection = styled.div`
    margin-bottom: 16px;

    @media screen and (min-width: 768px) {
        grid-column: 1 / -1;
        margin-bottom: 0;
    }
`;

export const TncPreviewCard = styled.div`
    width: 100%;
    height: 360px;
    border: 1px solid #e7e7f0;
    border-radius: 12px;
    overflow: auto;
    background: #f7f8fa;

    iframe {
        width: 100%;
        height: 720px;
        border: 0;
        display: block;
        background: #ffffff;
    }

    @media screen and (min-width: 768px) {
        height: 480px;

        iframe {
            height: 960px;
        }
    }
`;

export const TncHint = styled.div`
    margin-top: 8px;
    color: #5b5675;
    font-size: 12px;
    line-height: 16px;
`;

export const SubmitButtonWrapper = styled.div`
    margin-top: 12px;

    @media screen and (min-width: 768px) {
        max-width: 280px;
        margin-top: 20px;
    }
`;
